import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiPlay, FiCheck, FiStar, FiMail, FiChevronDown, FiSearch, FiFilter, FiMessageCircle, FiUser, FiPhone } from 'react-icons/fi';
import './App.css';
import HeroSection from './components/HeroSection';
import WorkshopTimer from './components/WorkshopTimer';
import { useWorkshopSchedule } from './hooks/useWorkshopSchedule';

// Payment Page URL
const PAYMENT_PAGE_URL = 'https://10xaifounder.com/healers_checkout';

// Updated Pabbly Webhook URL
const WEBHOOK_URL = 'https://connect.pabbly.com/webhook-listener/webhook/IjU3NjIwNTY1MDYzMDA0MzQ1MjZmIg_3D_3D_pc/IjU3NjcwNTZjMDYzMTA0MzM1MjZmNTUzNDUxMzYi_pc';

// WhatsApp Support Number
const WHATSAPP_NUMBER = '+919940890569';
const WHATSAPP_MESSAGE = encodeURIComponent(
  'Hi! I need help with the 10X AI Healer Workshop registration.'
);

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      ease: 'easeInOut'
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 12
    }
  }
};

const sectionFadeIn = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.7, ease: 'easeInOut' },
  viewport: { once: true, amount: 0.2 }
};

// Video testimonials data
const testimonialVideos = [
  {
    id: 1,
    url: 'https://youtu.be/vRzrNK9XPmQ?si=HcsiRvsrjOQhzfiO',
    embedId: 'vRzrNK9XPmQ',
    thumbnail: 'https://img.youtube.com/vi/vRzrNK9XPmQ/maxresdefault.jpg',
    title: 'Want More Customers? 🤔 See How Manivanan Did It with FunnelsDone',
    name: 'Dr. Valarmathi'
  },
  {
    id: 2,
    url: 'https://www.youtube.com/watch?v=v3EEtDIaC3w',
    embedId: 'v3EEtDIaC3w',
    thumbnail: 'https://img.youtube.com/vi/v3EEtDIaC3w/maxresdefault.jpg',
    title: 'How Funnels Done Transformed My Coaching Business',
    name: 'A Woman Entrepreneur Coach'
  },
  {
    id: 3,
    url: 'https://youtu.be/IrfPwkcSTB0?si=4zR7J5O2aIbdr7k0',
    embedId: 'IrfPwkcSTB0',
    thumbnail: 'https://img.youtube.com/vi/IrfPwkcSTB0/maxresdefault.jpg',
    title: 'From Struggling to 2 Lakhs: My Funnels Done Success Story',
    name: 'Bridal Lead Generation Expert Coach'
  },
  {
    id: 4,
    url: 'https://youtu.be/F26JiMS-w4I?si=5CoJ4w0AnUAMPYZm',
    embedId: 'F26JiMS-w4I',
    thumbnail: 'https://img.youtube.com/vi/F26JiMS-w4I/maxresdefault.jpg',
    title: 'Overcoming Coaching Business Challenges with Funnels Done',
    name: 'Coaching Business'
  },
  {
    id: 5,
    url: 'https://youtu.be/yWzJt7jv5CM?si=cFmZMEoNQgvFxmh4',
    embedId: 'yWzJt7jv5CM',
    thumbnail: 'https://img.youtube.com/vi/yWzJt7jv5CM/maxresdefault.jpg',
    title: 'How I Made 30 Lakhs in a Year by Helping a Lot of People as a Coach without Quiting my Job',
    name: 'Professor'
  },
  {
    id: 6,
    url: 'https://youtube.com/shorts/L0jJ2NHIQ8w?si=vDvB1EIcSDn0lzHX',
    embedId: 'L0jJ2NHIQ8w',
    thumbnail: 'https://img.youtube.com/vi/L0jJ2NHIQ8w/maxresdefault.jpg',
    title: 'The Secret Behind Mariyappan Business Growth 🔥 | FunnelsDone AI',
    name: 'Iniya Jewelers Madurai'
  }
];

// Client Feedback data
const clientFeedbacks = [
  {
    id: 1,
    name: 'Maria Sundar',
    role: 'Stock Trader Coach',
    image: 'https://storage.files-vault.com/uploads/1760942061-nsFEgCbsBd.webp',
    quote: 'Crossed 1000 members for level 1 in the last 60 days. On track for ₹10 Lakhs/month implementing this business model.'
  },
  {
    id: 2,
    name: 'A R Ahamed Junaith',
    role: 'English Confidence Building Coach',
    image: 'https://storage.files-vault.com/uploads/1760941910-ht57JBdJ4f.webp',
    quote: 'Crossed over ₹21+ Lakhs Revenue in 10 Months while working as a College Professor.'
  },
  {
    id: 3,
    name: 'Subiya Fathima',
    role: 'Bridal Lead Generation Expert',
    image: 'https://storage.files-vault.com/uploads/1760942062-cWbhjZc7n9.webp',
    quote: 'Sold my ₹5999 course to 19 people in a single day. Cleared all debts and school fees after struggling financially.'
  },
  {
    id: 4,
    name: 'Arjumand',
    role: 'Quran & Spiritual Coach',
    image: 'https://storage.files-vault.com/uploads/1760942064-I6HBtoRq5t.webp',
    quote: 'People are taking action and investing in getting a connection with Allah. It is more than coaching, it is a service to society.'
  },
  {
    id: 5,
    name: 'Manigandan',
    role: 'Course Student',
    image: 'https://storage.files-vault.com/uploads/1760942066-tiqyCho8Gf.webp',
    quote: 'Five star for Clarity, Dedication, and Service. Anyone willing to update their knowledge can opt for his courses blindly.'
  },
  {
    id: 6,
    name: 'Priya Sharma',
    role: 'IT Professional',
    image: null,
    quote: 'Work-Life Balance Restored. Income Doubled. Knowledge Impact Multiplied.'
  }
];

// FAQ data - For Healers
const faqData = [
  {
    id: 1,
    question: 'When and where is this workshop happening?',
    answer: "This is a 1.5-hour LIVE online workshop held **every Saturday from 8:00 PM to 9:30 PM IST** (India Standard Time). Once you register, you'll receive the secure joining link via email and WhatsApp."
  },
  {
    id: 2,
    question: 'Is this suitable for Healers & Wellness Practitioners?',
    answer: "Yes! This is specifically designed for Healers, Energy Healers, Therapists, Wellness Coaches, and Holistic Practitioners who want to build scalable systems, reach more people, and create predictable income without emotional burnout."
  },
  {
    id: 3,
    question: 'Do I need technical knowledge or coding skills?',
    answer: 'No. This workshop is designed for non-technical healers. We guide you through user-friendly AI tools that handle the "tech stuff" for you, so you can focus on your healing practice.'
  },
  {
    id: 4,
    question: "Will I get recordings if I can't attend live?",
    answer: 'To maintain the exclusivity and high-impact nature of the live session, recordings will not be provided. We encourage you to attend live to ask questions about your specific healing practice.'
  },
  {
    id: 5,
    question: 'How will this help scale my healing practice?',
    answer: 'We teach you how to set up an "AI Agent" that works 24/7 to nurture leads, answer inquiries, and book appointments for you, ensuring you never miss a potential client while freeing up your time and energy.'
  },
  {
    id: 6,
    question: 'What happens after I register?',
    answer: "You'll immediately receive a confirmation email and WhatsApp message with your unique joining link, bonus details, and instructions. You'll also get helpful reminders so you don't miss the live session."
  },
  {
    id: 7,
    question: 'What if I have questions about my specific healing practice?',
    answer: "Upon joining, you get Lifetime Access to our exclusive private community. You can ask questions there, and during the live Q&A session at the end of the workshop."
  },
  {
    id: 8,
    question: 'What is the registration fee?',
    answer: "The workshop fee is ₹49. This is a nominal amount to ensure serious participants and covers the cost of hosting and materials."
  }
];

// Tracking data extraction function
const getTrackingData = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const locale = navigator.language || 'en-US';

  let detectedCountry = 'Unknown';
  if (timezone && timezone.includes('/')) {
    const cityTimezone = timezone.split('/')[1];
    if (cityTimezone) {
      const timezoneCountryMap = {
        Kolkata: 'India',
        Mumbai: 'India',
        Delhi: 'India',
        Chennai: 'India',
        Bangalore: 'India',
        New_York: 'USA',
        Los_Angeles: 'USA',
        Chicago: 'USA',
        London: 'UK',
        Paris: 'France',
        Berlin: 'Germany',
        Tokyo: 'Japan',
        Singapore: 'Singapore',
        Dubai: 'UAE'
      };
      detectedCountry = timezoneCountryMap[cityTimezone] || 'Unknown';
    }
  }

  return {
    campaign_id: urlParams.get('campaign_id') || '',
    adset_id: urlParams.get('adset_id') || '',
    ad_id: urlParams.get('ad_id') || '',
    fbclid: urlParams.get('fbclid') || '',
    gclid: urlParams.get('gclid') || '',
    campaign_name: urlParams.get('campaign_name') || 'AI Healer Workshop Campaign',
    adset_name: urlParams.get('adset_name') || 'Workshop Ad Set',
    ad_name: urlParams.get('ad_name') || 'Workshop Ad',
    utm_source: urlParams.get('utm_source') || '',
    utm_medium: urlParams.get('utm_medium') || '',
    utm_campaign: urlParams.get('utm_campaign') || '',
    utm_term: urlParams.get('utm_term') || '',
    utm_content: urlParams.get('utm_content') || '',
    source: urlParams.get('source') || 'organic',
    medium: urlParams.get('medium') || '',
    country: detectedCountry,
    timezone: timezone,
    locale: locale,
    referrer: document.referrer || '',
    page_url: window.location.href,
    page_title: document.title,
    user_agent: navigator.userAgent,
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    timestamp: new Date().toISOString(),
    session_id: sessionStorage.getItem('session_id') || Date.now().toString()
  };
};

// Generate workshop batch ID
const generateWorkshopBatchId = (workshopDate) => {
  if (!workshopDate) return 'BATCH_UNKNOWN';
  const date = new Date(workshopDate);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `BATCH_${year}${month}${day}`;
};

// Get batch name
const getBatchName = (workshopDate) => {
  if (!workshopDate) return 'Saturday Unknown';
  const date = new Date(workshopDate);
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const dayName = dayNames[date.getDay()];
  const day = date.getDate();
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const monthName = monthNames[date.getMonth()];
  return `${dayName}, ${day}${getDaySuffix(day)} ${monthName}`;
};

// Get webinar batch ISO 8601 format
const getWebinarBatchISO = (workshopDate) => {
  if (!workshopDate) return new Date().toISOString();
  const date = new Date(workshopDate);
  date.setHours(20, 0, 0, 0); // 8:00 PM
  return date.toISOString();
};

function getDaySuffix(day) {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}

// WhatsApp Support Button Component
function WhatsAppSupportButton() {
  const handleWhatsAppClick = () => {
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');

    if (typeof fbq !== 'undefined') {
      fbq('track', 'Contact', {
        content_name: 'WhatsApp Support Click',
        content_category: 'Customer Support'
      });
    }
  };

  return (
    <motion.button
      className="whatsapp-support-button"
      onClick={handleWhatsAppClick}
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ type: 'spring', stiffness: 260, damping: 20, delay: 2 }}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
      aria-label="Contact us on WhatsApp"
    >
      <FiMessageCircle className="whatsapp-icon" />
      <span className="whatsapp-tooltip">Need Help? Chat with us!</span>
    </motion.button>
  );
}

function App() {
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [faqSearch, setFaqSearch] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [selectedRoleFilter, setSelectedRoleFilter] = useState('all');
  const [expandedFeedback, setExpandedFeedback] = useState({});

  const modalRef = useRef(null);
  const firstInputRef = useRef(null);

  const { workshopDetails } = useWorkshopSchedule();

  useEffect(() => {
    if (!sessionStorage.getItem('session_id')) {
      sessionStorage.setItem('session_id', Date.now().toString());
    }
  }, []);

  useEffect(() => {
    if (isFormModalOpen && firstInputRef.current) {
      firstInputRef.current.focus();
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsFormModalOpen(false);
        setSelectedVideo(null);
      }
    };

    if (isFormModalOpen || selectedVideo) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isFormModalOpen, selectedVideo]);

  const handleFaqSearch = useCallback(
    (value) => {
      setFaqSearch(value);
      if (searchTimeout) clearTimeout(searchTimeout);
      const newTimeout = setTimeout(() => {}, 300);
      setSearchTimeout(newTimeout);
    },
    [searchTimeout]
  );

  const filteredFaqs = useMemo(() => {
    if (!faqSearch.trim()) return faqData;
    return faqData.filter(
      (faq) =>
        faq.question.toLowerCase().includes(faqSearch.toLowerCase()) ||
        faq.answer.toLowerCase().includes(faqSearch.toLowerCase())
    );
  }, [faqSearch]);

  const filteredFeedbacks = useMemo(() => {
    if (selectedRoleFilter === 'all') return clientFeedbacks;
    return clientFeedbacks.filter((feedback) =>
      feedback.role.toLowerCase().includes(selectedRoleFilter.toLowerCase())
    );
  }, [selectedRoleFilter]);

  const uniqueRoles = useMemo(
    () => ['all', ...new Set(clientFeedbacks.map((f) => f.role))],
    []
  );

  const toggleFaq = (faqId) => setExpandedFaq(expandedFaq === faqId ? null : faqId);

  const toggleFeedbackExpanded = (feedbackId) =>
    setExpandedFeedback((prev) => ({ ...prev, [feedbackId]: !prev[feedbackId] }));

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) errors.name = 'Name is required';
    else if (formData.name.trim().length < 2) errors.name = 'Name must be at least 2 characters';

    if (!formData.email.trim()) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      errors.email = 'Please enter a valid email';

    if (!formData.phone.trim()) errors.phone = 'Phone number is required';
    else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, '')))
      errors.phone = 'Please enter a valid 10-digit phone number';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm() || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const trackingData = getTrackingData();
      const workshopBatchId = generateWorkshopBatchId(workshopDetails.date);
      const workshopBatchName = getBatchName(workshopDetails.date);
      const webinarBatchISO = getWebinarBatchISO(workshopDetails.date);

      // Clean phone number (remove any non-digits)
      const cleanPhone = formData.phone.replace(/\D/g, '');

      const webhookPayload = {
        full_name: formData.name,
        email: formData.email,
        phone: cleanPhone,
        workshop_batch: workshopBatchId,
        batch_name: workshopBatchName,
        batch_date: workshopDetails.formattedDate,
        batch_time: workshopDetails.timeString,
        webinar_batch_iso: webinarBatchISO,
        amount: '49',
        payment_status: 'pending',
        registration_type: 'paid_workshop',
        campaign_id: trackingData.campaign_id,
        adset_id: trackingData.adset_id,
        ad_id: trackingData.ad_id,
        campaign_name: trackingData.campaign_name,
        adset_name: trackingData.adset_name,
        ad_name: trackingData.ad_name,
        fbclid: trackingData.fbclid,
        gclid: trackingData.gclid,
        utm_source: trackingData.utm_source,
        utm_medium: trackingData.utm_medium,
        utm_campaign: trackingData.utm_campaign,
        utm_term: trackingData.utm_term,
        utm_content: trackingData.utm_content,
        country: trackingData.country,
        timezone: trackingData.timezone,
        locale: trackingData.locale,
        source: trackingData.source,
        medium: trackingData.medium,
        referrer: trackingData.referrer,
        page_url: trackingData.page_url,
        session_id: trackingData.session_id,
        registration_timestamp: trackingData.timestamp,
        product_name: '10x AI Healer Workshop',
        currency: 'INR'
      };

      try {
        const webhookResponse = await fetch(WEBHOOK_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify(webhookPayload)
        });

        if (!webhookResponse.ok) {
          console.warn('Webhook response not OK:', webhookResponse.status);
        }

        console.log('✅ Webhook data sent:', webhookPayload);
      } catch (webhookError) {
        console.warn('⚠️ Webhook submission failed:', webhookError);
      }

      if (typeof fbq !== 'undefined') {
        fbq('track', 'InitiateCheckout', {
          content_name: 'AI Healer Workshop Registration',
          content_category: 'Workshop',
          value: 49,
          currency: 'INR',
          ...(trackingData.campaign_id && { campaign_id: trackingData.campaign_id }),
          ...(trackingData.adset_id && { adset_id: trackingData.adset_id }),
          ...(trackingData.ad_id && { ad_id: trackingData.ad_id }),
          custom_batch_id: workshopBatchId,
          custom_batch_name: workshopBatchName
        });
      }

      setShowSuccess(true);
      setIsFormModalOpen(false);

      // Prepare payment page URL with parameters matching the required format
      const paymentParams = new URLSearchParams({
        full_name: formData.name,
        phone: cleanPhone,
        email: formData.email,
        company_name: workshopBatchName,
        campaign_name: trackingData.campaign_name,
        adset_name: trackingData.adset_name,
        ad_name: trackingData.ad_name,
        source: trackingData.source
      });

      const paymentUrl = `${PAYMENT_PAGE_URL}?${paymentParams.toString()}`;

      setTimeout(() => {
        window.location.href = paymentUrl;
      }, 1500);

    } catch (error) {
      console.error('❌ Form submission error:', error);
      alert('There was an error submitting the form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) setFormErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const getInitials = (name) =>
    name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);

  const handleManualModalOpen = () => {
    setIsFormModalOpen(true);
  };

  return (
    <>
      <div className="app">
        <AnimatePresence>
          {showSuccess && (
            <motion.div
              className="success-toast"
              initial={{ opacity: 0, y: -50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -50 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <FiCheck className="success-icon" />
              Registration successful! Redirecting to payment page...
            </motion.div>
          )}
        </AnimatePresence>

        <WhatsAppSupportButton />

        <HeroSection openRegistration={handleManualModalOpen} />

        {/* Problem Section */}
        <motion.section
          className="problem-section"
          initial="initial"
          whileInView="whileInView"
          transition={sectionFadeIn.transition}
          viewport={sectionFadeIn.viewport}
        >
          <div className="container">
            <motion.div className="section-content">
              <motion.h2 {...sectionFadeIn}>
                😰 Healers & Wellness Practitioners: Do You Face These Struggles?
              </motion.h2>

              <motion.div
                className="problem-grid"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
              >
                {[
                  {
                    emoji: '💸',
                    title: 'Irregular Income?',
                    desc: 'Income depends on sessions. Neenga irundha dhaan income varudhu? No sessions=No income.'
                  },
                  {
                    emoji: '😔',
                    title: 'Emotional Burnout?',
                    desc: 'You give energy, time, and emotional support every day. Ungal energy ellam others ku pogudhaa?'
                  },
                  {
                    emoji: '💰',
                    title: 'Undervalued & Undercharging?',
                    desc: '"Idhu romba costly aagidum" nu feel varudhaa? Your healing is powerful, but your pricing is not.'
                  },
                  {
                    emoji: '🔒',
                    title: 'Limited Reach?',
                    desc: 'One-to-one la mattum limit aagidudhu? Your knowledge can heal hundreds, even thousands.'
                  },
                  {
                    emoji: '⏰',
                    title: 'Time-for-Money Trap?',
                    desc: "Working hard but no scalability? Your time is limited, but your impact should not be."
                  },
                  {
                    emoji: '🎯',
                    title: 'Purpose vs Income Gap?',
                    desc: 'Purpose irundhaalum, without structure, impact stays small and income unstable.'
                  },
                  {
                    emoji: '📱',
                    title: 'No Digital Presence?',
                    desc: 'Still relying on word-of-mouth? Digital platform illama reach limited.'
                  },
                  {
                    emoji: '🤯',
                    title: 'Overwhelmed by Marketing?',
                    desc: 'Marketing, social media, follow-ups — everything manual ah panringa? Exhausting!'
                  },
                  {
                    emoji: '💔',
                    title: 'Want to Help More Without Exhausting Yourself?',
                    desc: 'You want to serve more people, but burnout prevents you from scaling your impact.'
                  }
                ].map((problem) => (
                  <motion.div
                    key={problem.title}
                    className="problem-card"
                    variants={itemVariants}
                    whileHover={{ y: -8, transition: { duration: 0.2 } }}
                  >
                    <h3>
                      {problem.emoji} {problem.title}
                    </h3>
                    <p>{problem.desc}</p>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </motion.section>

        {/* Solution Section */}
        <motion.section
          className="solution-section"
          initial="initial"
          whileInView="whileInView"
          transition={sectionFadeIn.transition}
          viewport={sectionFadeIn.viewport}
        >
          <div className="container">
            <motion.div className="section-content">
              <motion.h2 {...sectionFadeIn}>
                🎯 Old Healing Model vs 10xAI Healing Model
              </motion.h2>

              <div className="comparison-table">
                <motion.div
                  className="old-way"
                  {...sectionFadeIn}
                  transition={{ delay: 0.2, ...sectionFadeIn.transition }}
                >
                  <h3>❌ Struggling Healer (Old Way)</h3>
                  <ul>
                    <li>Income only when you're working</li>
                    <li>Emotional burnout from giving too much</li>
                    <li>Undercharging for your services</li>
                    <li>Limited to one-to-one sessions</li>
                    <li>No digital presence or systems</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="new-way"
                  {...sectionFadeIn}
                  transition={{ delay: 0.4, ...sectionFadeIn.transition }}
                >
                  <h3>✅ Smart AI Healer (New Way)</h3>
                  <ul>
                    <li>Scalable digital products for steady income</li>
                    <li>Help more people without burning out</li>
                    <li>Premium positioning with confident pricing</li>
                    <li>Reach hundreds through digital platforms</li>
                    <li>AI systems working 24/7 for you</li>
                  </ul>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* Mentor Section */}
        <section className="mentor-section py-16 bg-[#0b1324] text-gray-200">
          <div className="container mx-auto px-4">
            <motion.div
              className="mentor-content grid items-center gap-10 lg:grid-cols-2"
              initial="initial"
              whileInView="whileInView"
              transition={sectionFadeIn.transition}
              viewport={sectionFadeIn.viewport}
            >
              <div className="flex justify-center lg:justify-end">
                <motion.div
                  className="rounded-3xl border-2 border-orange-400 p-3 shadow-lg shadow-orange-500/20 bg-[#101c33]"
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <img
                    src="https://storage.files-vault.com/uploads/1761032692-VSdwsp8AG0.png"
                    alt="Abdul Hakkim - AI Business Mentor"
                    loading="lazy"
                    className="rounded-2xl w-full max-w-[360px] md:max-w-[400px] object-cover"
                  />
                </motion.div>
              </div>

              <div className="mentor-info max-w-xl mx-auto lg:mx-0">
                <motion.h2
                  className="text-3xl md:text-4xl font-bold text-orange-400 mb-6 flex items-center gap-2"
                  {...sectionFadeIn}
                >
                  🎯
                  <span>Meet Your Mentor</span>
                </motion.h2>

                <motion.p
                  className="text-base md:text-lg leading-relaxed mb-6 text-gray-300"
                  {...sectionFadeIn}
                  transition={{ delay: 0.1, ...sectionFadeIn.transition }}
                >
                  நான் Abdul Hakkim. பல Healers திறமை இருந்தும், சரியான Systems & Automation
                  இல்லாததால் கஷ்டப்படுவதைப் பார்த்திருக்கிறேன்.
                </motion.p>

                <motion.ul
                  className="space-y-4 text-gray-300 text-base md:text-lg"
                  variants={containerVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.2 }}
                >
                  {[
                    {
                      emoji: '👉',
                      color: 'text-yellow-400',
                      title: 'The Real Problem:',
                      desc: 'Healing gift இருக்கு, but scalable systems இல்லாததால் impact limited ஆகிறது.'
                    },
                    {
                      emoji: '✅',
                      color: 'text-green-400',
                      title: 'My Solution:',
                      desc: 'நான் உருவாக்கிய "AI Healing System" உங்களுக்காக 24/7 வேலை செய்து, predictable income கொடுக்கும்.'
                    },
                    {
                      emoji: '📈',
                      color: 'text-blue-400',
                      title: 'Proven Results:',
                      desc: 'Helped 100+ Healers build scalable practices crossing ₹1 Lakh/month consistently.'
                    },
                    {
                      emoji: '🗣️',
                      color: 'text-purple-400',
                      title: 'Easy Training:',
                      desc: 'Tamil-ல எளிய முறையில் AI கற்றுக்கொள்ளலாம். No Coding required.'
                    }
                  ].map((item) => (
                    <motion.li
                      key={item.title}
                      className="flex items-start gap-3"
                      variants={itemVariants}
                    >
                      <span className={`${item.color} text-xl mt-1`}>
                        {item.emoji}
                      </span>
                      <div className="flex-1">
                        <span className="font-medium">{item.title}</span>
                        <div className="text-gray-300 mt-1">{item.desc}</div>
                      </div>
                    </motion.li>
                  ))}
                </motion.ul>

                <motion.p
                  className="text-base md:text-lg leading-relaxed mt-6 text-gray-300"
                  {...sectionFadeIn}
                  transition={{ delay: 0.2, ...sectionFadeIn.transition }}
                >
                  உங்கள் Healing Practice-ஐ அடுத்த கட்டத்திற்கு கொண்டு செல்ல, இதுவே சரியான வாய்ப்பு. Let's
                  Automate! 🚀
                </motion.p>

                <motion.div
                  className="mt-6 flex flex-wrap gap-4"
                  {...sectionFadeIn}
                  transition={{ delay: 0.3, ...sectionFadeIn.transition }}
                >
                  <div className="flex items-center gap-2 text-sm bg-gray-700 px-3 py-1 rounded-full">
                    <FiStar className="text-yellow-400" />
                    <span>Specialist for Healers</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm bg-gray-700 px-3 py-1 rounded-full">
                    <FiStar className="text-yellow-400" />
                    <span>Proven Healing Systems</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm bg-gray-700 px-3 py-1 rounded-full">
                    <FiStar className="text-yellow-400" />
                    <span>Tamil AI Expert</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Testimonials Section */}
        <motion.section
          className="testimonials-section"
          initial="initial"
          whileInView="whileInView"
          transition={sectionFadeIn.transition}
          viewport={sectionFadeIn.viewport}
        >
          <div className="container">
            <motion.div className="section-content">
              <motion.h2 {...sectionFadeIn}>
                🎬 Healer Success Stories - Video Testimonials
              </motion.h2>

              <motion.p
                className="testimonials-subtitle"
                {...sectionFadeIn}
                transition={{ delay: 0.1, ...sectionFadeIn.transition }}
              >
                Real results from Tamil Healers who automated their practice
              </motion.p>

              <motion.div
                className="video-grid"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
              >
                {testimonialVideos.map((video) => (
                  <motion.div
                    key={video.id}
                    className="video-card"
                    onClick={() => {
                      setSelectedVideo(video);
                      if (typeof fbq !== 'undefined') {
                        fbq('track', 'ViewContent', {
                          content_name: video.title,
                          content_type: 'video'
                        });
                      }
                    }}
                    variants={itemVariants}
                    whileHover={{ scale: 1.02, y: -8 }}
                    whileTap={{ scale: 0.98 }}
                    tabIndex={0}
                    role="button"
                    aria-label={`Play testimonial video from ${video.name}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedVideo(video);
                      }
                    }}
                  >
                    <div className="video-thumbnail">
                      <img
                        src={video.thumbnail}
                        alt={`${video.title} thumbnail`}
                        loading="lazy"
                        width="300"
                        height="200"
                      />
                      <div className="play-overlay">
                        <FiPlay className="play-icon" />
                      </div>
                    </div>
                    <div className="video-info">
                      <h4>{video.title}</h4>
                      <p>{video.name}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </motion.section>

        {/* Client Feedback Section */}
        <motion.section
          className="client-feedback-section"
          initial="initial"
          whileInView="whileInView"
          transition={sectionFadeIn.transition}
          viewport={sectionFadeIn.viewport}
        >
          <div className="container">
            <motion.div className="section-content">
              <motion.h2 {...sectionFadeIn}>What Other Healers Say</motion.h2>

              <motion.p
                className="feedback-subtitle"
                {...sectionFadeIn}
                transition={{ delay: 0.1, ...sectionFadeIn.transition }}
              >
                Join hundreds of successful Tamil Healers using AI
              </motion.p>

              <motion.div
                className="feedback-filter"
                {...sectionFadeIn}
                transition={{ delay: 0.2, ...sectionFadeIn.transition }}
              >
                <FiFilter className="filter-icon" />
                <select
                  value={selectedRoleFilter}
                  onChange={(e) => setSelectedRoleFilter(e.target.value)}
                  className="role-filter-select"
                  aria-label="Filter by role"
                >
                  {uniqueRoles.map((role) => (
                    <option key={role} value={role}>
                      {role === 'all' ? 'All Roles' : role}
                    </option>
                  ))}
                </select>
              </motion.div>

              <motion.div
                className="feedback-grid"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
              >
                {filteredFeedbacks.map((feedback) => (
                  <motion.div
                    key={feedback.id}
                    className="feedback-card"
                    variants={itemVariants}
                    whileHover={{ scale: 1.02, y: -8 }}
                    whileTap={{ scale: 0.98 }}
                    tabIndex={0}
                    role="article"
                    aria-labelledby={`feedback-name-${feedback.id}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleFeedbackExpanded(feedback.id);
                      }
                    }}
                  >
                    <div className="feedback-image-container">
                      {feedback.image ? (
                        <img
                          src={feedback.image}
                          alt={`${feedback.name} - ${feedback.role}`}
                          loading="lazy"
                          width="300"
                          height="200"
                          className="feedback-rect-image"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className="avatar-fallback-rect">
                        {getInitials(feedback.name)}
                      </div>
                    </div>

                    <div className="feedback-info">
                      <h3 id={`feedback-name-${feedback.id}`}>
                        {feedback.name}
                      </h3>
                      <p className="feedback-role">{feedback.role}</p>
                    </div>

                    <div className="feedback-quote">
                      <p>
                        {expandedFeedback[feedback.id] ||
                        feedback.quote.length <= 140
                          ? feedback.quote
                          : `${feedback.quote.slice(0, 140)}...`}
                      </p>
                      {feedback.quote.length > 140 && (
                        <button
                          className="read-more-btn"
                          onClick={() => toggleFeedbackExpanded(feedback.id)}
                          aria-expanded={!!expandedFeedback[feedback.id]}
                          aria-controls={`feedback-quote-${feedback.id}`}
                        >
                          {expandedFeedback[feedback.id]
                            ? 'Read less'
                            : 'Read more'}
                        </button>
                      )}
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </motion.section>

        {/* Bonus Section */}
        <motion.section
          className="bonus-section"
          initial="initial"
          whileInView="whileInView"
          transition={sectionFadeIn.transition}
          viewport={sectionFadeIn.viewport}
        >
          <div className="container">
            <motion.div className="section-content">
              <motion.h2 {...sectionFadeIn}>
                🎁 Exclusive Bonus Vault for Healers (Worth ₹25,000)
              </motion.h2>

              <motion.div
                className="bonus-grid"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
              >
                {[
                  {
                    img: 'https://storage.files-vault.com/uploads/1760966854-BXbDBFXvqB.png',
                    emoji: '🤖',
                    title: 'AI Tools for Healers',
                    desc: 'Top 50+ AI tools to automate your client management, booking, and follow-ups (Worth ₹5,000)'
                  },
                  {
                    img: 'https://storage.files-vault.com/uploads/1760966871-XKw3XTHpgg.png',
                    emoji: '📊',
                    title: 'Healing Funnel Templates',
                    desc: 'Ready-to-use high-converting lead generation funnel templates for healers (Worth ₹8,000)'
                  },
                  {
                    img: 'https://storage.files-vault.com/uploads/1760976790-fakmsXQpOV.png',
                    emoji: '💬',
                    title: 'Private Healers Community',
                    desc: 'Lifetime access to exclusive Tamil Healers & Wellness Practitioners AI group (Worth ₹7,000)'
                  },
                  {
                    img: 'https://storage.googleapis.com/msgsndr/Te8QGpu12zbMxZIVQlZi/media/6974640bc1fa0c55da395de0.png',
                    emoji: '🗺️',
                    title: 'Free AI Roadmap',
                    desc: 'Personalized step-by-step PDF guide to launch your AI-powered Healing Practice (Worth ₹5,000)'
                  }
                ].map((bonus) => (
                  <motion.div
                    key={bonus.title}
                    className="bonus-item"
                    variants={itemVariants}
                    whileHover={{ y: -8, scale: 1.02 }}
                  >
                    <img
                      src={bonus.img}
                      alt={bonus.title}
                      className="bonus-image"
                      width="120"
                      height="160"
                      loading="lazy"
                    />
                    <h3>
                      {bonus.emoji} {bonus.title}
                    </h3>
                    <p>{bonus.desc}</p>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </motion.section>

        {/* FAQ Section */}
        <motion.section
          className="faq-section"
          initial="initial"
          whileInView="whileInView"
          transition={sectionFadeIn.transition}
          viewport={sectionFadeIn.viewport}
        >
          <div className="container">
            <motion.div className="section-content">
              <motion.h2 {...sectionFadeIn}>
                Frequently Asked Questions
              </motion.h2>

              <motion.p
                className="faq-subtitle"
                {...sectionFadeIn}
                transition={{ delay: 0.1, ...sectionFadeIn.transition }}
              >
                Got questions? We've got answers. Find everything you need to
                know about our program.
              </motion.p>

              <motion.div
                className="faq-search"
                {...sectionFadeIn}
                transition={{ delay: 0.2, ...sectionFadeIn.transition }}
              >
                <FiSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Search questions..."
                  value={faqSearch}
                  onChange={(e) => handleFaqSearch(e.target.value)}
                  className="search-input"
                  aria-label="Search frequently asked questions"
                />
              </motion.div>

              <div className="faq-accordion">
                {filteredFaqs.map((faq) => (
                  <motion.div key={faq.id} className="faq-item" layout>
                    <button
                      className="faq-question"
                      onClick={() => toggleFaq(faq.id)}
                      aria-expanded={expandedFaq === faq.id}
                      aria-controls={`faq-answer-${faq.id}`}
                      id={`faq-question-${faq.id}`}
                    >
                      <span>{faq.question}</span>
                      <FiChevronDown
                        className={`chevron-icon ${
                          expandedFaq === faq.id ? 'expanded' : ''
                        }`}
                      />
                    </button>

                    <AnimatePresence>
                      {expandedFaq === faq.id && (
                        <motion.div
                          id={`faq-answer-${faq.id}`}
                          className="faq-answer"
                          role="region"
                          aria-labelledby={`faq-question-${faq.id}`}
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: 'easeInOut' }}
                        >
                          <div
                            className="faq-answer-content"
                            dangerouslySetInnerHTML={{ __html: faq.answer }}
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </div>

              {filteredFaqs.length === 0 && (
                <motion.div
                  className="faq-no-results"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <p>
                    No questions found matching your search. Try different
                    keywords.
                  </p>
                </motion.div>
              )}
            </motion.div>
          </div>
        </motion.section>

        {/* Final CTA Section */}
        <motion.section
          className="final-cta"
          initial="initial"
          whileInView="whileInView"
          transition={sectionFadeIn.transition}
          viewport={sectionFadeIn.viewport}
        >
          <div className="container">
            <motion.div className="cta-content">
              <motion.h2 {...sectionFadeIn}>
                🔥 Limited Time Offer - Only 50 Seats Available!
              </motion.h2>

              <motion.p
                className="cta-subtitle"
                {...sectionFadeIn}
                transition={{ delay: 0.1, ...sectionFadeIn.transition }}
              >
                Workshop Value: ₹2,997 | Today: **Only ₹49!**
              </motion.p>

              <WorkshopTimer className="large" />

              <motion.button
                className="cta-primary large"
                onClick={() => {
                  handleManualModalOpen();
                  if (typeof fbq !== 'undefined') {
                    fbq('track', 'InitiateCheckout', {
                      content_name: 'AI Healer Workshop - Final CTA',
                      content_category: 'Workshop',
                      value: 49,
                      currency: 'INR'
                    });
                  }
                }}
                {...sectionFadeIn}
                transition={{ delay: 0.4, ...sectionFadeIn.transition }}
                whileHover={{ scale: 1.05, y: -4 }}
                whileTap={{ scale: 0.95 }}
              >
                Yes! Reserve My Seat for ₹49 🚀
              </motion.button>

              <motion.div
                className="guarantee-text"
                {...sectionFadeIn}
                transition={{ delay: 0.5, ...sectionFadeIn.transition }}
              >
                <FiCheck className="check-icon" />
                <span>Workshop Value ₹2,997 - Today Only ₹49</span>
              </motion.div>
            </motion.div>
          </div>
        </motion.section>

        {/* Registration Modal */}
        <AnimatePresence>
          {isFormModalOpen && (
            <motion.div
              className="modal-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={(e) => {
                if (e.target === e.currentTarget) setIsFormModalOpen(false);
              }}
            >
              <motion.div
                className="modal-content"
                id="registration-modal"
                initial={{ opacity: 0, scale: 0.8, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: 20 }}
                transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                ref={modalRef}
              >
                <div className="modal-header">
                  <h3>🚀 Register for the AI Workshop - ₹49!</h3>
                  <button
                    className="close-button"
                    onClick={() => setIsFormModalOpen(false)}
                    aria-label="Close registration form"
                  >
                    <FiX />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="registration-form">
                  <div className="form-group">
                    <label htmlFor="name">
                      <FiUser className="input-icon" />
                      Full Name *
                    </label>
                    <input
                      ref={firstInputRef}
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={(e) =>
                        handleInputChange('name', e.target.value)
                      }
                      placeholder="Enter your full name"
                      className={formErrors.name ? 'error' : ''}
                      aria-describedby={
                        formErrors.name ? 'name-error' : undefined
                      }
                    />
                    {formErrors.name && (
                      <span
                        id="name-error"
                        className="error-message"
                        role="alert"
                      >
                        {formErrors.name}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">
                      <FiMail className="input-icon" />
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) =>
                        handleInputChange('email', e.target.value)
                      }
                      placeholder="Enter your email address"
                      className={formErrors.email ? 'error' : ''}
                      aria-describedby={
                        formErrors.email ? 'email-error' : undefined
                      }
                    />
                    {formErrors.email && (
                      <span
                        id="email-error"
                        className="error-message"
                        role="alert"
                      >
                        {formErrors.email}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">
                      <FiPhone className="input-icon" />
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) =>
                        handleInputChange('phone', e.target.value)
                      }
                      placeholder="Enter your 10-digit phone number"
                      className={formErrors.phone ? 'error' : ''}
                      aria-describedby={
                        formErrors.phone ? 'phone-error' : undefined
                      }
                    />
                    {formErrors.phone && (
                      <span
                        id="phone-error"
                        className="error-message"
                        role="alert"
                      >
                        {formErrors.phone}
                      </span>
                    )}
                  </div>

                  <div className="workshop-batch-info">
                    <div className="batch-label">Workshop Batch:</div>
                    <div className="batch-value">{getBatchName(workshopDetails.date)}</div>
                  </div>

                  <button
                    type="submit"
                    className="submit-button"
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? 'Processing...'
                      : 'Continue to Payment - ₹49 🚀'}
                  </button>

                  <p className="form-note">
                    After clicking continue, you'll be redirected to the payment page to complete your registration.
                  </p>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Video Modal */}
        <AnimatePresence>
          {selectedVideo && (
            <motion.div
              className="video-modal-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={(e) => {
                if (e.target === e.currentTarget) setSelectedVideo(null);
              }}
            >
              <motion.div
                className="video-modal-content"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              >
                <div className="video-modal-header">
                  <h3>{selectedVideo.title}</h3>
                  <button
                    className="close-button"
                    onClick={() => setSelectedVideo(null)}
                    aria-label="Close video"
                  >
                    <FiX />
                  </button>
                </div>

                <div className="video-embed-container">
                  <iframe
                    src={`https://www.youtube.com/embed/${selectedVideo.embedId}?autoplay=1&rel=0`}
                    title={selectedVideo.title}
                    allow="accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;picture-in-picture"
                    allowFullScreen
                    loading="lazy"
                  ></iframe>
                </div>

                <div className="video-fallback">
                  <p>
                    Can't view the video?{' '}
                    <a
                      href={selectedVideo.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open on YouTube
                    </a>
                  </p>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Sticky Footer */}
        <motion.div
          className="sticky-footer"
          initial={{ y: '100%' }}
          animate={{ y: '0%' }}
          transition={{ duration: 0.7, ease: 'easeInOut', delay: 1.5 }}
        >
          <motion.div
            className="footer-info-box"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 2.2 }}
          >
            <div className="date">Next Workshop:</div>
            <div className="time">{workshopDetails.formattedDate}</div>
          </motion.div>

          <motion.button
            className="footer-cta-button"
            onClick={() => {
              handleManualModalOpen();
              if (typeof fbq !== 'undefined') {
                fbq('track', 'InitiateCheckout', {
                  content_name: 'AI Healer Workshop - Sticky Footer CTA',
                  content_category: 'Workshop',
                  value: 49,
                  currency: 'INR'
                });
              }
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            animate={{
              scale: [1, 1.03, 1],
              transition: {
                duration: 1.5,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 2.5
              }
            }}
          >
            Register for ₹49 🚀
          </motion.button>
        </motion.div>

        <footer className="footer">
          <div className="container">
            <div className="footer-content">
              <p>&copy;2026 10xAI Founder. All rights reserved.</p>
              <p
                style={{ fontSize: '0.8rem', opacity: '0.7', marginTop: '1rem' }}
              >
                This site is not part of the Facebook website or Facebook Inc.
                Additionally, this site is NOT endorsed by Facebook. FACEBOOK is
                a trademark of FACEBOOK, Inc.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}

export default App;